import { useMemo, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type MailingDetail, type MailingRecipientRow } from '../api/client'
import { fmtInt } from '../lib/format'

function fmtUsd(n: number, dp = 2): string {
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp })}`
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
function fmtMonth(ym: string | null): string {
  if (!ym) return 'Unknown'
  const [y, m] = ym.split('-')
  return `${MONTHS[Number(m) - 1] ?? m} ${y}`
}

type SortKey = 'account_number' | 'name' | 'expired_on' | 'matched' | 'converted' | 'payment_type'

export function MailingResultsPage() {
  const { id } = useParams()
  const queryClient = useQueryClient()
  const { data: m, isLoading } = useQuery({
    queryKey: ['mailing', id],
    queryFn: () => api.get<MailingDetail>(`/api/mailings/${id}`),
    refetchInterval: 30_000,
  })
  const [windowDays, setWindowDays] = useState<number | ''>('')
  const [sortKey, setSortKey] = useState<SortKey>('account_number')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')

  const saveWindow = useMutation({
    mutationFn: (days: number) =>
      api.put<MailingDetail>(`/api/mailings/${id}/attribution-window`, { attribution_window_days: days }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['mailing', id] }),
  })

  const sorted = useMemo(() => {
    const rows = [...(m?.recipients ?? [])]
    const dir = sortDir === 'asc' ? 1 : -1
    const val = (r: MailingRecipientRow): string | number => {
      switch (sortKey) {
        case 'matched': return r.matched ? 1 : 0
        case 'converted': return r.converted ? 1 : 0
        case 'name': return (r.name ?? '').toLowerCase()
        case 'expired_on': return r.expired_on ?? ''
        case 'payment_type': return (r.payment_type ?? '').toLowerCase()
        default: return r.account_number
      }
    }
    rows.sort((a, b) => {
      const va = val(a), vb = val(b)
      if (va < vb) return -1 * dir
      if (va > vb) return 1 * dir
      return a.account_number < b.account_number ? -1 : 1
    })
    return rows
  }, [m?.recipients, sortKey, sortDir])

  if (isLoading || !m) return <div className="label text-paper-trace p-8">Loading…</div>

  const matchRate = m.recipient_count ? (m.matched_count / m.recipient_count) * 100 : 0

  function toggleSort(k: SortKey) {
    if (k === sortKey) setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
    else { setSortKey(k); setSortDir(k === 'converted' || k === 'matched' ? 'desc' : 'asc') }
  }
  const arrow = (k: SortKey) => (k === sortKey ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '')
  const th = (k: SortKey, label: string) => (
    <th
      className="label py-2 px-3 font-normal text-left cursor-pointer hover:text-amber-glow select-none"
      onClick={() => toggleSort(k)}
    >
      {label}{arrow(k)}
    </th>
  )

  return (
    <div className="p-8 space-y-6 max-w-[1400px]">
      <header className="border-b border-ink-500 pb-6">
        <Link to="/mailings" className="label text-paper-trace hover:text-amber-glow">← Mailings</Link>
        <h1 className="font-serif text-[36px] leading-tight tracking-tight text-paper mt-2">{m.name}</h1>
        <div className="text-[12px] text-paper-trace font-mono">
          {m.slug} · {m.publication_name} · mailed {m.mail_date} · window {m.attribution_window_days}d · {m.status}
        </div>
      </header>

      <div className="grid grid-cols-4 gap-4">
        <Stat label="Recipients" value={fmtInt(m.recipient_count)} />
        <Stat label="Match rate" value={`${matchRate.toFixed(1)}%`} sub={`${fmtInt(m.matched_count)} matched`} />
        <Stat label="Conversions" value={fmtInt(m.conversions_count)} accent />
        <Stat label="Revenue" value={fmtUsd(m.conversions_revenue, 0)} />
      </div>

      <div className="border border-ink-500 bg-ink-800/40 p-4 text-[13px] font-mono">
        <div className="label mb-2">New vs renewal</div>
        <div className="flex gap-6 text-paper-dim">
          <span>new <span className="text-paper">{fmtInt(m.new_or_renewal.new)}</span></span>
          <span>renewal <span className="text-paper">{fmtInt(m.new_or_renewal.renewal)}</span></span>
          <span>unknown <span className="text-paper">{fmtInt(m.new_or_renewal.unknown)}</span></span>
        </div>
      </div>

      {/* Expired-month breakdown */}
      <div>
        <div className="label mb-2">Cohort by expired month</div>
        <div className="border border-ink-500 bg-ink-800/40">
          <table className="w-full text-[13px] font-mono">
            <thead>
              <tr className="border-b border-ink-500/60 bg-ink-800/60">
                <th className="label py-2 px-3 font-normal text-left">Expired</th>
                <th className="label py-2 px-3 font-normal text-right">Recipients</th>
                <th className="label py-2 px-3 font-normal text-right">Conversions</th>
                <th className="label py-2 px-3 font-normal text-right">Revenue</th>
                <th className="label py-2 px-3 font-normal text-right">Online</th>
                <th className="label py-2 px-3 font-normal text-right">Check</th>
                <th className="label py-2 px-3 font-normal text-right">Other</th>
              </tr>
            </thead>
            <tbody>
              {m.month_breakdown.map((b) => (
                <tr key={b.month ?? 'unknown'} className="border-b border-ink-500/30 hover:bg-ink-700/30">
                  <td className="px-3 py-1.5 text-paper">{fmtMonth(b.month)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-paper-dim">{fmtInt(b.recipients)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-amber-glow">{fmtInt(b.conversions)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{fmtUsd(b.revenue, 0)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-paper-dim">{fmtInt(b.online)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-radar-cyan">{fmtInt(b.check)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-paper-trace">{fmtInt(b.other)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex items-end gap-3">
        <label className="label">Attribution window (days)
          <input
            type="number"
            className="block mt-1 bg-ink-900 border border-ink-500 px-2 py-1 font-mono text-paper w-28"
            value={windowDays === '' ? m.attribution_window_days : windowDays}
            onChange={(e) => setWindowDays(e.target.value === '' ? '' : Number(e.target.value))}
          />
        </label>
        <button
          className="border border-amber-glow/50 text-amber-glow px-3 py-1 font-mono text-[12px] hover:bg-amber-glow/10"
          onClick={() => saveWindow.mutate(windowDays === '' ? m.attribution_window_days : windowDays)}
        >
          Apply
        </button>
      </div>

      {/* Recipient table (sortable) */}
      <div className="border border-ink-500 bg-ink-800/40">
        <table className="w-full text-[13px] font-mono">
          <thead>
            <tr className="border-b border-ink-500/60 bg-ink-800/60">
              {th('account_number', 'Account #')}
              {th('name', 'Name')}
              {th('expired_on', 'Expired')}
              {th('matched', 'Matched')}
              {th('converted', 'Converted')}
              {th('payment_type', 'Pay type')}
            </tr>
          </thead>
          <tbody>
            {sorted.map((r) => (
              <tr key={r.account_number} className="border-b border-ink-500/30 hover:bg-ink-700/30">
                <td className="px-3 py-1.5 tabular-nums">
                  {r.subscriber_id
                    ? <Link to={`/subscribers/${r.subscriber_id}`} className="text-paper hover:text-amber-glow">{r.account_number}</Link>
                    : <span className="text-paper-dim">{r.account_number}</span>}
                </td>
                <td className="px-3 py-1.5 text-paper-dim">{r.name ?? '—'}</td>
                <td className="px-3 py-1.5 text-paper-dim tabular-nums">{fmtMonth(r.expired_on ? r.expired_on.slice(0, 7) : null)}</td>
                <td className="px-3 py-1.5">{r.matched ? <span className="text-radar-green">yes</span> : <span className="text-paper-trace">no</span>}</td>
                <td className="px-3 py-1.5">{r.converted ? <span className="text-amber-glow">✓</span> : <span className="text-paper-trace">—</span>}</td>
                <td className="px-3 py-1.5">
                  {r.payment_class === 'check'
                    ? <span className="px-1.5 py-0.5 rounded bg-radar-cyan/20 text-radar-cyan">{r.payment_type}</span>
                    : <span className="text-paper-dim">{r.payment_type ?? '—'}</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function Stat({ label, value, sub, accent }: { label: string; value: string; sub?: string; accent?: boolean }) {
  return (
    <div className="border border-ink-500 bg-ink-800/40 p-4">
      <div className="label">{label}</div>
      <div className={`font-serif text-[28px] tabular-nums ${accent ? 'text-amber-glow' : 'text-paper'}`}>{value}</div>
      {sub && <div className="text-[11px] text-paper-trace font-mono">{sub}</div>}
    </div>
  )
}
